import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ProximityEntity } from '_entities';
import { resolveProximityFilter } from 'utils/resolveProximityFilter';
import { httpClient } from 'lib';
import * as Styled from './old.styles';

const buildUrl = (endpoint, queryParts) => {
  const filtered = queryParts.filter(Boolean);
  if (filtered.length === 0) return endpoint;
  const sep = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${sep}${filtered.join('&')}`;
};

const extractCount = (response) => response?.meta?.pagination?.count ?? response?.resultcount ?? null;

function AmountText({ amount, isManageAssignment, activeTab, mapEndpoint, fetchMapTotal, amountLabel }) {
  const lastAppliedProximity = useSelector(ProximityEntity.selectors.getLastAppliedProximity);
  const { search } = useLocation();
  const [total, setTotal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const isMapTab = activeTab === 'map';

    if (!mapEndpoint || !isMapTab) {
      setTotal(null);
      setSelected(null);
      setIsLoading(false);
      return undefined;
    }

    const lastAppliedFilter = resolveProximityFilter(lastAppliedProximity);

    const needsTotalFetch = Boolean(fetchMapTotal);
    const needsSelectedFetch = Boolean(lastAppliedFilter);

    if (!needsTotalFetch && !needsSelectedFetch) {
      setTotal(null);
      setSelected(null);
      setIsLoading(false);
      return undefined;
    }

    const currentQuery = search.replace(/^\?/, '');
    const totalUrl = needsTotalFetch ? buildUrl(mapEndpoint, [currentQuery]) : null;
    const selectedUrl = needsSelectedFetch ? buildUrl(mapEndpoint, [currentQuery, lastAppliedFilter]) : null;

    let cancelled = false;
    setIsLoading(true);

    Promise.all([
      totalUrl ? httpClient.get(totalUrl) : Promise.resolve(null),
      selectedUrl ? httpClient.get(selectedUrl) : Promise.resolve(null),
    ])
      .then(([totalResult, selectedResult]) => {
        if (cancelled) return;
        setTotal(needsTotalFetch ? extractCount(totalResult) : null);
        setSelected(needsSelectedFetch ? extractCount(selectedResult) : null);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setTotal(null);
        setSelected(null);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, search, mapEndpoint, fetchMapTotal, lastAppliedProximity]);

  if (!(amount || amount === 0)) return null;

  if (isLoading) {
    return <Styled.AmountText>Loading...</Styled.AmountText>;
  }

  const isMapTab = activeTab === 'map';
  const displayTotal = fetchMapTotal ? total : amount;

  if (mapEndpoint && isMapTab && displayTotal !== null && displayTotal !== undefined) {
    const headline =
      selected !== null ? `proximity: ${selected} of ${displayTotal} retailers` : `${displayTotal} retailers`;
    return <Styled.AmountText>{headline}</Styled.AmountText>;
  }

  return <Styled.AmountText>{`${amount} ${amountLabel} ${isManageAssignment ? '(retailers)' : ''}`}</Styled.AmountText>;
}

AmountText.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isManageAssignment: PropTypes.bool,
  activeTab: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  mapEndpoint: PropTypes.string,
  fetchMapTotal: PropTypes.bool,
  // Noun used in the fallback "N <label>" headline (List/Media tabs). Defaults to "items"; callers
  // whose rows are retailers (e.g. the Retailers list) pass "retailers".
  amountLabel: PropTypes.string,
};

AmountText.defaultProps = {
  amount: null,
  isManageAssignment: false,
  activeTab: null,
  mapEndpoint: null,
  fetchMapTotal: false,
  amountLabel: 'items',
};

export default AmountText;
